/**
 * WorkflowSearchableSelect.jsx
 *
 * A lightweight searchable dropdown used within the WorkflowDesigner.
 * Fetches options from a provided async loader function and renders a
 * type-ahead list. Scoped inside the wfd- namespace so it inherits
 * the global button reset from WorkflowDesigner.scss.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaSpinner, FaChevronDown, FaTimes } from 'react-icons/fa';

/**
 * @param {object}   props
 * @param {string}   props.value           - Current value (id/string stored in config)
 * @param {string}   props.displayValue    - Human-readable label shown in the field
 * @param {function} props.onSelect        - Called with (value, label) when an option is picked
 * @param {function} props.loadOptions     - Async (search: string) => [{ id, label, sub? }]
 * @param {string}   [props.placeholder]
 * @param {boolean}  [props.disabled]
 */
const WorkflowSearchableSelect = ({
    value,
    displayValue,
    onSelect,
    loadOptions,
    placeholder = 'Search...',
    disabled = false
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const debounceRef = useRef(null);
    const containerRef = useRef(null);

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const fetchOptions = useCallback(async (term) => {
        setLoading(true);
        try {
            const results = await loadOptions(term);
            setOptions(results || []);
        } catch {
            setOptions([]);
        } finally {
            setLoading(false);
        }
    }, [loadOptions]);

    const handleSearch = (e) => {
        const term = e.target.value;
        setSearch(term);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchOptions(term), 300);
    };

    const handleSelect = (opt) => {
        onSelect(String(opt.id), opt.label);
        setOpen(false);
        setSearch('');
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onSelect('', '');
    };

    return (
        <div className="wfd-searchable-select" ref={containerRef}>
            <div
                className={`wfd-searchable-trigger ${open ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
                onClick={() => { if (!disabled) { if (!open) fetchOptions(search); setOpen(o => !o); } }}
            >
                <span className={`wfd-searchable-label ${!displayValue ? 'placeholder' : ''}`}>
                    {displayValue || placeholder}
                </span>
                <div className="wfd-searchable-controls">
                    {value && !disabled && (
                        <button className="wfd-searchable-clear" onClick={handleClear} title="Clear">
                            <FaTimes />
                        </button>
                    )}
                    <FaChevronDown className={`wfd-searchable-chevron ${open ? 'flipped' : ''}`} />
                </div>
            </div>

            {open && (
                <div className="wfd-searchable-dropdown">
                    <div className="wfd-searchable-input-wrap">
                        <input
                            autoFocus
                            type="text"
                            className="wfd-searchable-input"
                            placeholder="Type to filter..."
                            value={search}
                            onChange={handleSearch}
                        />
                    </div>
                    <div className="wfd-searchable-list">
                        {loading ? (
                            <div className="wfd-searchable-loading">
                                <FaSpinner className="wfd-spinner" /> Loading...
                            </div>
                        ) : options.length === 0 ? (
                            <div className="wfd-searchable-empty">No results found</div>
                        ) : (
                            options.map(opt => (
                                <button
                                    key={opt.id}
                                    className={`wfd-searchable-option ${String(opt.id) === String(value) ? 'selected' : ''}`}
                                    onClick={() => handleSelect(opt)}
                                >
                                    <span className="wfd-searchable-option-label">{opt.label}</span>
                                    {opt.sub && <span className="wfd-searchable-option-sub">{opt.sub}</span>}
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default WorkflowSearchableSelect;
